import _sortBy from 'lodash/sortBy'
import _cloneDeep from 'lodash/cloneDeep'
import {
  makeRegex,
  defaultInsuranceMatcherConfig,
  InsuranceMatcherConfig,
  InsuranceMatcherGroup,
} from './helpers'

type InsuranceSplit = {
  highlightedInsurances: ProviderInsurance[]
  listDisplayInsurances: ProviderInsurance[]
  groups: InsuranceMatcherGroup[]
  groupRegexes: RegExp[]
}

const useInsuranceSplit = (
  providerInsurances: ProviderInsurance[],
  selectedInsurance = '',
  matcherConfig: InsuranceMatcherConfig = defaultInsuranceMatcherConfig
): ProviderInsurance[][] => {
  const { highlightedInsurances, listDisplayInsurances } = splitInsurances(
    providerInsurances,
    selectedInsurance,
    matcherConfig
  )

  return [highlightedInsurances, listDisplayInsurances]
}

// One line of names for provider cards: the pills, then the plans that are not
// folded into a pill. A plan folds into its group's pill even when the 5-pill cap
// sent it straight to the list, and a group without a pill shows only its first
// plan, so a carrier never shows twice
export const getInsuranceNamesForCard = (
  providerInsurances: ProviderInsurance[],
  matcherConfig: InsuranceMatcherConfig = defaultInsuranceMatcherConfig,
  selectedInsurance = ''
): string[] => {
  const {
    highlightedInsurances,
    listDisplayInsurances,
    groups,
    groupRegexes,
  } = splitInsurances(providerInsurances, selectedInsurance, matcherConfig)
  const listedGroupIndexes = new Set<number>()

  const otherInsurances = listDisplayInsurances.filter(
    ({ insuranceProvider }) => {
      const groupIndex = findGroupIndex(groupRegexes, insuranceProvider?.name)

      if (groupIndex === -1) {
        return true
      }

      if (groups[groupIndex].isGrouped || listedGroupIndexes.has(groupIndex)) {
        return false
      }

      listedGroupIndexes.add(groupIndex)

      return true
    }
  )

  return [...highlightedInsurances, ...otherInsurances].map(
    ({ insuranceProvider }) => insuranceProvider.name
  )
}

const splitInsurances = (
  providerInsurances: ProviderInsurance[],
  selectedInsurance: string,
  matcherConfig: InsuranceMatcherConfig
): InsuranceSplit => {
  const sortedInsurances = getSortedInsurances(providerInsurances)

  const formattedSortedInsurances = []

  /** move user selected insurance to top if it exists and the provider supports it. So that
   * it is selected among the top rated insurances first. Else we run a risk of filling the max allowed top
   * rated insurance (currently 5) before we reach selected insurance
   */
  for (const insurance of sortedInsurances) {
    if (insurance.insuranceProvider.id === selectedInsurance) {
      formattedSortedInsurances.unshift(insurance)
    } else {
      formattedSortedInsurances.push(insurance)
    }
  }

  return makeTwoSeparateListsOfInsurances(
    formattedSortedInsurances,
    selectedInsurance,
    matcherConfig
  )
}

const makeTwoSeparateListsOfInsurances = (
  sortedInsurances,
  selectedInsurance,
  { matcherList, topRatedMatcherList }: InsuranceMatcherConfig
): InsuranceSplit => {
  const maxHighlightedInsurances = 5
  const topRatedRegex = makeRegex('multistring', topRatedMatcherList, null)
  const clonedGroupedInsurance = _cloneDeep(matcherList)
  const groupRegexes = clonedGroupedInsurance.map(({ matcher }) =>
    makeRegex(
      matcher.isExactMatch ? 'exact' : 'multistring',
      matcher.match,
      matcher.exclude
    )
  )

  // never name a pill after a parent plan the provider doesn't accept
  clonedGroupedInsurance.forEach((group) => {
    if (
      group.parentNames &&
      !hasParentPlan(group.parentNames, sortedInsurances)
    ) {
      group.alias = null
    }
  })

  const [topInsurances, remainingInsurances] = sortedInsurances.reduce(
    ([a, b], { insuranceProvider }) => {
      const isUserSelectedInsurance =
        insuranceProvider?.id === selectedInsurance

      if (
        (topRatedRegex.test(insuranceProvider?.name) ||
          isUserSelectedInsurance) &&
        a.length < maxHighlightedInsurances
      ) {
        const indexInGroupedInsurance = findGroupIndex(
          groupRegexes,
          insuranceProvider?.name
        )
        updateGroupedInsuranceInfo(
          indexInGroupedInsurance,
          clonedGroupedInsurance,
          insuranceProvider,
          a,
          b,
          isUserSelectedInsurance
        )
      } else {
        // not top rated
        b.push({ insuranceProvider })
      }

      return [a, b]
    },
    [[], []]
  )
  const topInsurancesCount = topInsurances.length

  if (topInsurancesCount >= 2) {
    // bring the user selected insurance/ group to the top of the highlighted list before displaying
    bringUserSelectedInsuranceToTheTop(selectedInsurance, topInsurances)
  }

  return {
    highlightedInsurances: topInsurances,
    listDisplayInsurances:
      topInsurancesCount > 0 ? remainingInsurances : sortedInsurances,
    groups: clonedGroupedInsurance,
    groupRegexes,
  }
}

// the first matching group wins, so group order in the config matters
const findGroupIndex = (groupRegexes: RegExp[], name: string): number =>
  groupRegexes.findIndex((groupRegex) => groupRegex.test(name))

const hasParentPlan = (
  parentNames: string[],
  insurances: ProviderInsurance[]
): boolean =>
  insurances.some(({ insuranceProvider }) =>
    parentNames.some(
      (parentName) =>
        parentName.toLowerCase() ===
        insuranceProvider?.name?.trim().toLowerCase()
    )
  )

export const getSortedInsurances = (
  providerInsurances: ProviderInsurance[]
): ProviderInsurance[] => {
  const filteredProviderInsurances = providerInsurances?.filter(
    (insurance) => insurance?.insuranceProvider?.name !== 'SelfPay'
  )
  const insurancesSorted = _sortBy(filteredProviderInsurances, [
    (providerInsurance) =>
      providerInsurance.insuranceProvider?.name?.toLowerCase(),
  ])

  return insurancesSorted
}

const updateGroupedInsuranceInfo = (
  indexInGroupedInsurance,
  clonedGroupedInsurance,
  insuranceProvider,
  highlightedInsuranceArray,
  listDisplayInsuranceArray,
  isUserSelectedInsurance
) => {
  if (
    indexInGroupedInsurance !== -1 &&
    clonedGroupedInsurance[indexInGroupedInsurance].isGrouped === null
  ) {
    // is first identified in a group and is userSelected
    // modifying the display name of highlighted for UI, unless the group has no alias
    // for this provider (its parent plan is missing), then it keeps its own name
    const highlightedInsurance = {
      ...insuranceProvider,
      name:
        clonedGroupedInsurance[indexInGroupedInsurance].alias ??
        insuranceProvider.name,
    }

    highlightedInsuranceArray.push({ insuranceProvider: highlightedInsurance })
    listDisplayInsuranceArray.push({ insuranceProvider })

    clonedGroupedInsurance[indexInGroupedInsurance].isGrouped = true
    clonedGroupedInsurance[indexInGroupedInsurance].idToBeHighlighted =
      highlightedInsurance.id
  } else if (indexInGroupedInsurance === -1) {
    // is top rated but not in a group
    highlightedInsuranceArray.push({ insuranceProvider: insuranceProvider })
  } else {
    // group already started, add the grouped to the remaining list but...
    // if it's a user selected insurance, change id of insurance to be highlighted to this one
    updateHighlightedIdToUserSelectedId(
      isUserSelectedInsurance,
      clonedGroupedInsurance,
      indexInGroupedInsurance,
      highlightedInsuranceArray,
      insuranceProvider
    )

    listDisplayInsuranceArray.push({ insuranceProvider })
  }
}

const updateHighlightedIdToUserSelectedId = (
  isUserSelectedInsurance,
  clonedGroupedInsurance,
  indexInGroupedInsurance,
  highlightedInsuranceArray,
  insuranceProvider
) => {
  if (isUserSelectedInsurance) {
    const foundIndex = highlightedInsuranceArray.findIndex(
      ({ insuranceProvider }) =>
        insuranceProvider.id ===
        clonedGroupedInsurance[indexInGroupedInsurance].idToBeHighlighted
    )

    if (foundIndex !== -1) {
      highlightedInsuranceArray[foundIndex].insuranceProvider.id =
        insuranceProvider.id
    }
  }
}

const bringUserSelectedInsuranceToTheTop = (
  selectedInsurance,
  highlightedInsurances
) => {
  const userSelectedInsuranceIndex = highlightedInsurances.findIndex(
    ({ insuranceProvider }) => insuranceProvider?.id === selectedInsurance
  )

  if (userSelectedInsuranceIndex !== -1) {
    const userSelectedInsurance = highlightedInsurances.splice(
      userSelectedInsuranceIndex,
      1
    )
    highlightedInsurances.splice(0, 0, userSelectedInsurance[0])
  }
}

export default useInsuranceSplit
